import { isStructurallyValidLanguageTag } from '@formatjs/intl-getcanonicallocales';
import { EmbeddedMessage, EmbeddedMessageMedium, WhatsApp } from 'constants/messageMediums';
import { TemplateType } from 'constants/templateTypes';
import { selectFeatureFlags, selectUserRbacPermissions } from 'core/userContext';
import { get, isEmpty } from 'lodash';
import { NBA_PREFIX } from 'modules/ai/nba/utils/transformCampaign';
import { getPreviewHTMLTemplateUrl } from 'modules/campaigns/lib/utils/template';
import { ContactCard } from 'modules/contactCards/types';
import { DataFeed } from 'modules/dataFeeds/types';
import { Experiment } from 'modules/domains/experiment';
import { Locale } from 'modules/domains/template';
import {
  EmailContentDefinition,
  EmbeddedAction,
  EmbeddedContentDefinition,
  HtmlEditor,
  InAppContentDefinition,
  InvalidTemplateLocale,
  InvalidTemplateName,
  MESSAGE_FORMAT,
  MediumContent,
  MessageMedium,
  MessageType,
  PushContentDefinition,
  SMSContentDefinition,
  Unrenderable,
  UnrenderableFieldName,
  WebPushContentDefinition,
  WhatsAppContentDefinition,
} from 'modules/graphql/types';
import { RefObject } from 'react';
import { useSelector } from 'react-redux';
import { ApiResponse, getRaw, postRaw } from 'services/api';
import { BaseOption } from 'ui/selects';
import { PROJECT_PERMISSIONS, canManageMessagingByMedium } from 'utils/permissions';
import { isTextOnly } from 'utils/stringHelpers';

import { CLICK_LOCATION_LIMIT } from '../EditWebPush/constants';
import { DEFAULT_LINK_SHORTENING_TRACKING_DOMAIN } from '../EditSMS/constants';
import { ListOption } from '../context/SendTestMsgContext';
import { ERROR_TYPES, errorThemes } from './ValidationErrorTheme';
import {
  DATAFEED_DATA_URL,
  DRAG_AND_DROP_TEMPLATES_URL,
  GQL_CONTENT_ERRORS,
  GQL_CONTENT_WARNINGS,
  GSM7_MULTIPLE_MESSAGE_MAX_LENGTH,
  GSM7_SINGLE_MESSAGE_MAX_LENGTH,
  RENDERED_DATA_URL,
  SUPPORTED_DESIGN_ERROR_FIELD_NAMES,
  UCS2_MULTIPLE_MESSAGE_MAX_LENGTH,
  UCS2_SINGLE_MESSAGE_MAX_LENGTH,
  USER_DATA_URL,
  USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER,
  USER_FRIENDLY_FIELD_NAMES,
} from './constants';
import { displayContent } from './content';
import {
  ACTION_BUTTON_RADIO_OPTIONS,
  ContentIdKey,
  ErrorThemeDisplay,
  ErrorValidationConstructReturn,
  ErrorValidationFieldNameConstruct,
  FailureErrorReason,
  FieldNames,
  GqlInvalidContentReason,
  GqlUpsertTemplateFailure,
  GqlUpsertTemplateFailureTypes,
  GqlUpsertTemplateInvalidContentFailureTypes,
  InitTemplateTrigger,
  InterruptionLevel,
  MessagingPermissionOptions,
  PreviewWithDataErrorTypes,
  SelectedMessageMedium,
  TemplateAndContentFormDataLocale,
  TemplateCampaign,
  TemplateEditViews,
  UserProfileDataType,
  ValidationError,
  ValidationFailureErrors,
} from './types';

/**
 * Formats the Message types for the BasicSelect used in the MessageTypeSelect component
 */
export const formatMessageTypes = (messageTypes: MessageType[]) => {
  return messageTypes.map((messageType) => {
    const { messageChannel } = messageType;
    const bulletOperator = '\u{2219}';
    return {
      // note: while the rest of the double line displays info about the message channel, the id is that of the message type.
      doubleLine: `ID: ${messageType.id} ${bulletOperator} ${messageChannel?.name} ${bulletOperator} Channel: ${messageChannel?.channelType}`,
      id: messageType.id.toString(),
      label: messageType.name,
      value: messageType.id,
    };
  });
};

/**
 * Formats the iOS Interruption Levels types for the BasicSelect used in the InterruptionSelect component
 */
export const formatInterruptionTypes = (
  interruptionLevels: InterruptionLevel[],
  criticalEnabled = false,
) => {
  return interruptionLevels.map((interruptionLevel) => {
    return {
      doubleLine: `${
        interruptionLevel.isDisabled ? interruptionLevel.disabledSubtext : interruptionLevel.subtext
      }`,
      id: interruptionLevel.id.toString(),
      label: interruptionLevel.label,
      value: interruptionLevel.value,
      isDisabled: !criticalEnabled && interruptionLevel.value === 'Critical',
    };
  });
};

/**
 * Formats the data feed options for MultiSelect used in the DataFeedsFormSection component
 */
export const formatDataFeedOptions = (
  dataFeeds: { [key: string]: DataFeed } = {},
  searchTerm?: string,
) => {
  const keys = Object.keys(dataFeeds);
  return keys
    .map((dataFeedKey) => ({
      id: dataFeedKey,
      label: dataFeeds[dataFeedKey].name,
      value: dataFeedKey,
      doubleLine: dataFeeds[dataFeedKey].alias,
    }))
    .filter(
      ({ label, doubleLine }) =>
        label.toLowerCase().includes(searchTerm?.toLowerCase() ?? '') ||
        doubleLine?.toLowerCase().includes(searchTerm?.toLowerCase() ?? ''),
    );
};

/**
 * Returns an HtmlEditor indicating which content editor to render
 * @param {SelectedMessageMedium} medium - any valid SelectedMessageMedium (expected Email or InApp)
 * @param {HtmlEditor} editorSelected - any valid HtmlEditor (enum)
 * @param {boolean} hasDragAndDropMetadata - indicator of whether drag and drop has been initialized
 * @param {string} htmlContent - html value
 * @param {boolean} wysiwygEnabled - indicator of whether wysiwyg is enabled for the project
 * @returns {HtmlEditor} HtmlEditor indicating which content editor to render
 */
export const getContentEditor = (
  medium: SelectedMessageMedium,
  editorSelected: HtmlEditor | undefined,
  hasDragAndDropMetadata: boolean,
  htmlContent: string,
  wysiwygEnabled: boolean,
) => {
  if (editorSelected === HtmlEditor.Bee || hasDragAndDropMetadata) return HtmlEditor.Bee;
  if (editorSelected === HtmlEditor.Wysiwyg && !wysiwygEnabled) return HtmlEditor.Ace;
  if (!editorSelected && !!htmlContent && medium === MessageMedium.InApp && isTextOnly(htmlContent))
    return HtmlEditor.Ace;
  if (!editorSelected && !!htmlContent) return wysiwygEnabled ? HtmlEditor.Wysiwyg : HtmlEditor.Ace;
  return editorSelected;
};

/**
 * Returns a boolean indicating whether or not to render a form element in edit mode
 * @param {string} view - any valid TemplateEditViews
 * @param {boolean} isEditModeContext - indicator of manual selection of edit mode
 * @returns {boolean} boolean indicating whether or not to render a form element in edit mode
 */
export const getIsEditMode = (view: string, isEditModeContext: boolean) => {
  return view !== TemplateEditViews.settings || isEditModeContext;
};

/**
 * Returns the string to display for number of data feeds enabled for a template
 */
export const getDataFeedNumberDisplay = (dataFeedIds: number[] = []) =>
  `${dataFeedIds.length} feed${dataFeedIds.length > 1 ? 's' : ''}`;

/**
 * Returns a valid locale string  for Intl formatting
 * @param {string} [locale] - any valid locale
 * @returns {string} a locale string that is valid for Intl formatting
 */
export const getValidFormatLocale = (locale = '') => {
  const isValidLocale = isStructurallyValidLanguageTag(locale);
  // For Intl formatting, 'default' refers to Intl.DateTimeFormat().resolvedOptions().locale, which
  // - In Chrome: (chrome://settings/languages) the language labeled "This language is used to
  //   display the Google Chrome UI"
  // - In Firefox: (about:preferences) the language selected for "Choose the language used to
  //   display menus, messages, and notifications from Firefox"

  return isValidLocale ? locale : 'default';
};

/**
 * Returns a localized 12-hour time of day
 * @param {Date} [date] - any valid date
 * @param {string} [locale] - any valid locale
 * @returns {string} localized 12-hour time of day
 */
export const getTime = (date = new Date(), locale = '') => {
  const formatLocale = getValidFormatLocale(locale);

  return new Intl.DateTimeFormat(formatLocale, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

/**
 * Returns a localized 12-hour time of day without the dayPeriod indicator
 * @param {Date} [date] - any valid date
 * @param {string} [locale] - any valid locale
 * @returns {string} localized 12-hour time of day with hr and min separated by literal
 */
export const getTimeSimple = (date = new Date(), locale = '') => {
  const formatLocale = getValidFormatLocale(locale);
  const timeParts = new Intl.DateTimeFormat(formatLocale, {
    hour: 'numeric',
    minute: '2-digit',
  }).formatToParts(date);

  return timeParts
    .filter((part) => part.type !== 'dayPeriod')
    .map((part) => part.value)
    .join('')
    .trim();
};

/**
 * Returns a date string with the abbreviated weekday, month, and day only
 * @param {Date} [date] - any valid date
 * @param {string} [locale] - any valid locale
 * @returns {string} localized date string with the abbreviated weekday, month, and day only
 */
export const getDateShort = (date = new Date(), locale = '') => {
  const formatLocale = getValidFormatLocale(locale);

  return new Intl.DateTimeFormat(formatLocale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date);
};

/**
 * Returns a date string with weekday, month, and day only
 * @param {Date} [date] - any valid date
 * @param {string} [locale] - any valid locale
 * @returns {string} localized date string with weekday, month, and day only
 */
export const getDate = (date = new Date(), locale = '') => {
  const formatLocale = getValidFormatLocale(locale);

  return new Intl.DateTimeFormat(formatLocale, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(date);
};

/**
 * Returns a date string with weekday, month, day, and year
 * @param {Date} [date] - any valid date
 * @param {string} [locale] - any valid locale
 * @returns {string} localized date string with weekday, month, day, and year
 */
export const getDateFull = (date = new Date(), locale = '') => {
  const formatLocale = getValidFormatLocale(locale);

  return new Intl.DateTimeFormat(formatLocale, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

/**
 * Returns a localized date & time
 * @param {string} [date] - any valid date (as a string)
 * @param {string} [locale] - any valid locale
 * @returns {string} a localized date & time
 */
export const getDateTime = (date = new Date().toString(), locale = '') => {
  const dateTime = new Date(date);
  const formatLocale = getValidFormatLocale(locale);
  const formattedDateTime = new Intl.DateTimeFormat(formatLocale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(dateTime);

  return formattedDateTime;
};

/**
 * Returns a localized relative date/time offset and an actual date
 * @param {string} [date] - any valid date (as a string)
 * @param {string} [locale] - any valid locale
 * @returns {object} an object containing a localized relative date/time offset and an actual date
 */
export const getRelativeTimeInfo = (date = new Date().toString(), locale = '') => {
  const formatLocale = getValidFormatLocale(locale);
  const now = new Date();
  const then = new Date(date);
  const elapsedTime = now.getTime() - then.getTime();
  const minutes = Math.floor(elapsedTime / (1000 * 60));
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const relTime = new Intl.RelativeTimeFormat(formatLocale, {
    numeric: 'always',
    style: 'long',
  });
  const tooltip = getDateTime(date, locale);

  if (days > 0) return { label: relTime.format(-days, 'day'), tooltip };
  if (hours > 0) return { label: relTime.format(-hours, 'hour'), tooltip };
  return { label: relTime.format(-minutes, 'minute'), tooltip };
};

/**
 * Returns a localized relative day
 * @param {string} [date] - any valid date (as a string)
 * @param {string} [locale] - any valid locale
 * @returns {string} a localized relative day ('today')
 */
export const getRelativeDayInfo = (date = new Date().toString(), locale = '') => {
  const now = new Date();
  const then = new Date(date);
  const elapsedTime = now.getTime() - then.getTime();
  const days = Math.floor(elapsedTime / (1000 * 60 * 60 * 24));
  const formatLocale = getValidFormatLocale(locale);
  const relTime = new Intl.RelativeTimeFormat(formatLocale, {
    numeric: 'auto',
    style: 'long',
  });
  const fullDate = getDateTime(date, locale);

  if (days > 5) return fullDate;
  return relTime.format(days, 'day');
};

/**
 * Returns a boolean indicating whether or not the template has content
 * @param {SelectedMessageMedium} medium - any valid medium
 * @param {MediumContent} mediumData - the medium data of the indicated medium
 * @returns {boolean} a boolean indicating whether or not the template has content
 */
export const getTemplateHasContent = (
  medium: SelectedMessageMedium,
  mediumData: MediumContent = {},
) => {
  switch (medium) {
    case MessageMedium.Email: {
      // the initial state for Email and InApp (when Drag and Drop is selected) will have
      // beeBuilderMetadata, but no html, so we need to check for both (since Bee does not generate
      // html until you save and you cannot save until you have content)
      const emailMediumData = mediumData as EmailContentDefinition;
      return !!emailMediumData.html?.trim() || !!emailMediumData.beeBuilderMetadata?.trim();
    }
    case MessageMedium.InApp: {
      const inAppMediumData = mediumData as InAppContentDefinition;
      const { beeBuilderMetadata, html, messageFormat, ottPayload, payload } = inAppMediumData;
      const { Everything, Html, Json } = MESSAGE_FORMAT;

      const parseJson = (payloadString?: string | null) => {
        if (!payloadString) return null;
        try {
          return JSON.parse(payloadString);
        } catch (e) {
          return null;
        }
      };

      const hasHtmlContent =
        (messageFormat === Html || messageFormat === Everything) &&
        (!!html?.trim() || !!beeBuilderMetadata?.trim());

      const hasRawJsonContent =
        (messageFormat === Json || messageFormat === Everything) && !isEmpty(parseJson(payload));

      return hasHtmlContent || hasRawJsonContent || !!ottPayload;
    }
    case MessageMedium.Push:
      return !!(mediumData as PushContentDefinition).message?.trim();
    case MessageMedium.SMS: {
      const smsMediumData = mediumData as SMSContentDefinition;
      return !!smsMediumData.message?.trim() || !!smsMediumData.verifiedContactCardId;
    }
    case MessageMedium.WebPush: {
      const webPushMediumData = mediumData as WebPushContentDefinition;
      return !!webPushMediumData.message?.trim() || !!webPushMediumData.title?.trim();
    }
    case WhatsApp: {
      const whatsAppContent = mediumData as WhatsAppContentDefinition;
      const { message, header, buttons, footer } = whatsAppContent;
      return (
        (buttons || [])?.length > 0 ||
        Object.values(header || {}).some((value) => !!value) ||
        !!message ||
        !!footer
      );
    }
    case EmbeddedMessage: {
      const embeddedContent = mediumData as EmbeddedContentDefinition;
      const { title, body, elements, payload } = embeddedContent;
      return (
        Object.values(elements || {}).some((value) => !!value) || !!title || !!body || !!payload
      );
    }
    default:
      // UnselectedMessageMedium.Unselected
      return false;
  }
};

/**
 * Generates readable string of snippets in list form
 */
const snippetListToTextHelper = (snippets: string[]) => {
  if (snippets.length === 1) {
    return snippets[0];
  }
  const snippetsCopy = [...snippets];
  const lastSnippet = snippetsCopy.splice(snippetsCopy.length - 1, 1)[0];
  return `${snippetsCopy.join(', ')}, and ${lastSnippet}`;
};

export const getUnrenderableErrorMessage = (err: Unrenderable) => {
  if (
    (err.fieldName as string) === UnrenderableFieldName.smsMessage ||
    (err.fieldName as string) === UnrenderableFieldName.smsMessageDataFeed
  ) {
    return 'Not saved. Check Message for invalid Handlebars and retry.';
  } else {
    return `Not saved. Check ${err.fieldName} for invalid content and retry.`;
  }
};

// right now i think were redirecting this so this might not be needed anymore
export const getErrorMessage = (
  err: GqlUpsertTemplateFailure | GqlInvalidContentReason,
): string => {
  switch (err.__typename) {
    case GqlUpsertTemplateFailureTypes.INVALID_TEMPLATE_NAME: {
      return (err as unknown as InvalidTemplateName).reason;
    }
    case GqlUpsertTemplateFailureTypes.INVALID_TEMPLATE_LOCALE: {
      return (err as unknown as InvalidTemplateLocale).reason;
    }
    case GqlUpsertTemplateInvalidContentFailureTypes.MISSING_SNIPPETS: {
      return `Not saved. ${snippetListToTextHelper(err.snippets)} ${
        err.snippets.length > 1 ? `don't` : `doesn't`
      } exist in your project.`;
    }
    case GqlUpsertTemplateInvalidContentFailureTypes.INVALID_SNIPPET_PARAMS: {
      return `Not saved. ${snippetListToTextHelper(err.snippetsWithParams)} ${
        err.snippetsWithParams.length > 1 ? 'are' : 'is'
      } missing at least one variable.`;
    }
    case GqlUpsertTemplateInvalidContentFailureTypes.UNRENDERABLE: {
      return getUnrenderableErrorMessage(err);
    }
    case GqlUpsertTemplateFailureTypes.TEMPLATE_NOT_FOUND: {
      return `Cannot save. This template may belong to another project, or it may have been deleted.`;
    }

    case GqlUpsertTemplateInvalidContentFailureTypes.MISSING_UNSUBSCRIBE_URL: {
      return `Template content must contain an unsubscribe url.`;
    }
    case GqlUpsertTemplateInvalidContentFailureTypes.INVALID_SUBJECT: {
      return `Template subject field is required.`;
    }
    case GqlUpsertTemplateInvalidContentFailureTypes.JS_IN_ANCHOR_TAG: {
      return `Details not saved. Check ${err?.fieldName ?? ''} for javascript in anchor tag`;
    }
    case GqlUpsertTemplateInvalidContentFailureTypes.JS_IN_IFRAME_TAG: {
      return `Details not saved. Check ${err?.fieldName ?? ''} for javascript in Iframe tag`;
    }
    case GqlUpsertTemplateInvalidContentFailureTypes.ILLEGAL_SCRIPT: {
      return `Details not saved. Check ${err?.fieldName ?? ''} for an illegal script tag`;
    }
    case GqlUpsertTemplateInvalidContentFailureTypes.INVALID_ATTRIBUTE: {
      return `Details not saved. Check ${err?.fieldName ?? ''} ${err?.tagName ? `in ${err?.tagName} tag` : ''} for invalid attribute`;
    }
    case GqlUpsertTemplateInvalidContentFailureTypes.WHATS_APP_CATEGORY_UPDATE_ATTEMPTED_AFTER_SUBMISSION: {
      return `Category can't be changed after a template is submitted to Meta for approval.`;
    }
    default: {
      return 'An error occurred';
    }
  }
};

export const getDataFeedData = (
  templateId: number,
  userData: Record<string, any> = {},
): Promise<ApiResponse> =>
  postRaw(DATAFEED_DATA_URL, {
    templateId,
    userData,
  });

export const getUserData = (
  userId: string,
  userDataType: UserProfileDataType,
): Promise<ApiResponse> => {
  return getRaw(`${USER_DATA_URL}?${userDataType}=${encodeURIComponent(userId)}`);
};

export const getExperimentAttributeFromCampaign = (
  campaign: TemplateCampaign,
  attribute: keyof Experiment,
) => {
  const { experiments } = campaign;
  if (Array.isArray(experiments) && experiments.length > 0) {
    return experiments[0][attribute];
  }
  return null;
};

export const getDragAndDropTemplates = (): Promise<ApiResponse> => {
  return getRaw(DRAG_AND_DROP_TEMPLATES_URL);
};

export const getDoesCampaignHaveExperiments = (campaign: TemplateCampaign) =>
  !!getExperimentAttributeFromCampaign(campaign, 'id');

/**
 * Returns a string containing the first characters of the first and last contiguous strings
 * @param {string} fullName - a name (of one or more space-separated contiguous strings)
 * @returns {string} a string containing the initials of the fullName
 */
export const getInitials = (fullName: string) => {
  const nameParts = fullName.trim().split(' ');

  return [
    nameParts[0].substring(0, 1),
    ...(nameParts.length ? [nameParts[nameParts.length - 1].substring(0, 1)] : []),
  ].join('');
};

/**
 * Determines if a given string contains handlebars
 * @param {string} msg - any text string
 * @returns {boolean} indication of the existence of handlebars
 */
export const hasHandlebars = (msg = '') => {
  const regexp = /{{.*}}/;
  return regexp.test(msg);
};

/**
 * Determines if a given string contains only GSM7 characters
 * @param {string} msg - any text string
 * @returns {boolean} indication of the existence of only GSM7 characters
 */
export const isGSM7 = (msg: string) => {
  if (msg && msg.length > 0) {
    // altered version of http://stackoverflow.com/questions/12673120/how-to-detect-non-gsm-7-bit-alphabet-characters-in-input-field
    const regexp =
      /^[A-Za-z0-9 \r\n@£€$¥èéùìòÇØøÅå\u0394_\u03a6\u0393\u039b\u03a9\u03a0\u03a8\u03a3\u0398\u039e\u00a4ÆæßÉ!"#$%&'()*+,\-./:;<=>?¡ÄÖÑÜ§¿äöñüà^{}\\[~\]|\u20ac]*$/;
    return regexp.test(msg);
  }
  return true;
};

export const getCharLimitPerMsg = (msg: string, totalMessageLength: number) => {
  const IS_GSM7 = isGSM7(msg);

  if (IS_GSM7) {
    return totalMessageLength > GSM7_SINGLE_MESSAGE_MAX_LENGTH
      ? GSM7_MULTIPLE_MESSAGE_MAX_LENGTH
      : GSM7_SINGLE_MESSAGE_MAX_LENGTH;
  } else {
    return totalMessageLength > UCS2_SINGLE_MESSAGE_MAX_LENGTH
      ? UCS2_MULTIPLE_MESSAGE_MAX_LENGTH
      : UCS2_SINGLE_MESSAGE_MAX_LENGTH;
  }
};

/**
 * With GSM7 For some characters, such as '{' and ']', an escape code is required - so even in a GSM-7 encoded message these characters will be encoded using two characters
 * These characters can be seen here in the Basic Character Set Extension Table: https://en.wikipedia.org/wiki/GSM_03.38#GSM_7-bit_default_alphabet_and_extension_table_of_3GPP_TS_23.038_.2F_GSM_03.38
 * */
export const getGSM7CharacterLength = (message: string = '') => {
  if (isGSM7(message)) {
    const extendedCharsInMessage = message.replace(/[^^{}[~\\\]|€]/g, '');
    return message.length + extendedCharsInMessage.length;
  }
  return message.length;
};

export const getMsgCount = (msg: string, totalMessageLength: number) => {
  const IS_GSM7 = isGSM7(msg);

  const singleMsgLength = IS_GSM7 ? GSM7_SINGLE_MESSAGE_MAX_LENGTH : UCS2_SINGLE_MESSAGE_MAX_LENGTH;
  const multipleMsgLength = IS_GSM7
    ? GSM7_MULTIPLE_MESSAGE_MAX_LENGTH
    : UCS2_MULTIPLE_MESSAGE_MAX_LENGTH;

  return totalMessageLength <= singleMsgLength
    ? 1
    : Math.ceil(totalMessageLength / multipleMsgLength);
};

export const isRequired = (value = '') => value.length > 0;

/** Uploads the data feed and user data information to the BE to generate the content for preview
 *  Can also be used to retrieve data after the upload
 */
export const getContentWithData = (
  templateId: number,
  fields: Record<string, any>,
  dataFeed: Record<string, any>,
  userData: Record<string, any>,
): Promise<ApiResponse> =>
  postRaw(RENDERED_DATA_URL, {
    jsonTestData: {
      dataFeedJson: dataFeed,
      userJson: userData,
    },
    payload: {}, // required (will be overwritten by fields if present (for InApp))
    templateId,
    ...fields,
  });

/** Fetches the content for preview that was generated in the function above
 *  Main difference is that this call will also return merge parameters properly filled out
 */
export const getContentWithDataAndMergeParameters = (
  templateId: number,
  locale: string,
): Promise<ApiResponse> => getRaw(getPreviewHTMLTemplateUrl(templateId, locale));

export const truncateText = (text = '', length: number) =>
  text?.length > length ? `${text.substring(0, length)}...` : text;

/** To prevent app crashing, fall back to truncated url when user enters wrong url or just plain text. */
export const getDomain = (url = '') => {
  try {
    const domain = new URL(url);
    return domain.origin;
  } catch (e) {
    return truncateText(url, CLICK_LOCATION_LIMIT);
  }
};

/**
 * This function is for when inserting snippet into <InputText> where the cursor is.
 * @param {string} copiedSnippet
 * @param {ref} ref to the specific <InputText>
 * @param {textInput} text entered in <InputText>
 * @returns {string} entire string combined with copiedSnippet at the cursor location and existing text in the <InputText>.
 */
export const concatSnippetWithTextInput = (
  copiedSnippet: string,
  textInput: string | ACTION_BUTTON_RADIO_OPTIONS,
  ref: RefObject<HTMLTextAreaElement> | RefObject<HTMLInputElement>,
) => {
  const cursorPosition = ref?.current?.selectionStart || 0;

  if (textInput.length === 0) {
    return copiedSnippet;
  } else {
    const textBeforeCursorPosition = textInput.substring(0, cursorPosition);
    const textAfterCursorPosition = textInput.substring(cursorPosition, textInput.length);
    const messageAndSnippet = textBeforeCursorPosition + copiedSnippet + textAfterCursorPosition;
    return messageAndSnippet;
  }
};

/**
 * Downloads a file containing html
 * @param {string} filename a filename.extension
 * @param {string} html
 */
export const downloadHtml = (fileName: string, html: string) => {
  const file = new File([html], fileName, { type: 'text/html' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(file);

  link.href = url;
  link.download = file.name;
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export enum ByteUnit {
  B = 'byte',
  KB = 'kilobyte',
  MB = 'megabyte',
  GB = 'gigabyte',
}

const getByteUnitSize = (byteSize: number, unit: ByteUnit) => {
  const increment = 1024;
  switch (unit) {
    case ByteUnit.KB:
      return byteSize / increment;
    case ByteUnit.MB:
      return byteSize / increment ** 2;
    case ByteUnit.GB:
      return byteSize / increment ** 3;
    default:
      // ByteUnit.B
      return byteSize;
  }
};

export const getByteSizeInfo = (str = '', unit = ByteUnit.KB, locale = '') => {
  const formatLocale = getValidFormatLocale(locale);
  const byteSize = new Blob([str]).size;
  const byteUnitSize = getByteUnitSize(byteSize, unit);

  return {
    formatted: new Intl.NumberFormat(formatLocale, {
      style: 'unit',
      unit,
      maximumFractionDigits: 2,
    }).format(byteUnitSize),
    size: byteUnitSize,
  };
};

export const getListLabelFromOptions = (listOptions: ListOption[], id: number) =>
  listOptions.find((listOption) => listOption.id === `${id}`)?.label;

type ContentValidationPushType = {
  contentId?: number;
  errorContainer: ValidationError[];
  errorType: GqlUpsertTemplateInvalidContentFailureTypes;
  fieldName?: string;
  invalidButtonActions?: EmbeddedAction[];
  isRoku?: boolean;
  key: keyof FailureErrorReason;
  locale?: string;
  message?: string;
  reason: FailureErrorReason;
  subMessage?: string;
  index?: number;
};

export const handleContentValidationPush = ({
  contentId,
  errorContainer,
  errorType,
  fieldName,
  invalidButtonActions,
  isRoku,
  key,
  locale,
  message,
  reason,
  subMessage,
  index,
}: ContentValidationPushType): void => {
  const errorValidation: Record<string, any> = {};
  if (reason.__typename === errorType) {
    if (fieldName) errorValidation.fieldName = fieldName;
    if (message) errorValidation.errorMessage = message;
    if (contentId) errorValidation.contentId = contentId;
    if (index !== undefined) errorValidation.index = index;
    if (reason[key]) errorValidation[key] = `${reason[key]}`;
    if (subMessage) errorValidation.subMessage = subMessage;
    if (isRoku) errorValidation.isRoku = isRoku;
    if (invalidButtonActions) errorValidation.invalidButtonActions = invalidButtonActions;

    errorContainer.push({
      ...errorValidation,
      contentId,
      locale,
      __typename: reason.__typename,
    } as ValidationError);
  }
};

const convertKeyType = (key: string) => key as keyof FailureErrorReason;

const constructErrorMessage = (fieldName: boolean, errorMessage: string) =>
  fieldName ? errorMessage : 'An unknown error occurred';

const getFriendlyFieldName = (fieldName: FieldNames | undefined) =>
  fieldName ? USER_FRIENDLY_FIELD_NAMES[fieldName] : fieldName;

const constructFriendlyFieldNameErrorMessages = (
  fieldName: FieldNames | undefined,
): Record<
  Extract<
    GqlUpsertTemplateInvalidContentFailureTypes,
    | GqlUpsertTemplateInvalidContentFailureTypes.UNRENDERABLE
    | GqlUpsertTemplateInvalidContentFailureTypes.UNRENDERABLE_WITH_INDEX
    | GqlUpsertTemplateInvalidContentFailureTypes.INVALID_EMAIL
    | GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_CONTENT
    | GqlUpsertTemplateInvalidContentFailureTypes.MISSING_MESSAGE_OR_SILENT_PUSH
    | GqlUpsertTemplateInvalidContentFailureTypes.INVALID_SNIPPET_USAGE
    | GqlUpsertTemplateInvalidContentFailureTypes.INVALID_HANDLEBAR_COMPLEXITY
    | GqlUpsertTemplateInvalidContentFailureTypes.INVALID_HANDLEBAR_COUNT
  >,
  string
> => ({
  [GqlUpsertTemplateInvalidContentFailureTypes.UNRENDERABLE]: constructErrorMessage(
    !!fieldName,
    `Content in ${getFriendlyFieldName(fieldName)} isn't formatted correctly`,
  ),
  [GqlUpsertTemplateInvalidContentFailureTypes.UNRENDERABLE_WITH_INDEX]: constructErrorMessage(
    !!fieldName,
    `Content in ${getFriendlyFieldName(fieldName)} isn't formatted correctly`,
  ),
  [GqlUpsertTemplateInvalidContentFailureTypes.INVALID_HANDLEBAR_COUNT]: constructErrorMessage(
    !!fieldName,
    fieldName === FieldNames.BUTTONS
      ? `Merge parameters are not supported for Buttons`
      : `Only one merge parameter allowed in ${getFriendlyFieldName(fieldName)}`,
  ),
  [GqlUpsertTemplateInvalidContentFailureTypes.INVALID_SNIPPET_USAGE]: constructErrorMessage(
    !!fieldName,
    `Invalid content in ${getFriendlyFieldName(fieldName)}`,
  ),
  [GqlUpsertTemplateInvalidContentFailureTypes.INVALID_HANDLEBAR_COMPLEXITY]: constructErrorMessage(
    !!fieldName,
    `Invalid content in ${getFriendlyFieldName(fieldName)}`,
  ),
  [GqlUpsertTemplateInvalidContentFailureTypes.INVALID_EMAIL]: constructErrorMessage(
    !!fieldName,
    `Unauthorized or invalid email in ${getFriendlyFieldName(fieldName)}`,
  ),
  [GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_CONTENT]: constructErrorMessage(
    !!fieldName,
    `${getFriendlyFieldName(fieldName)} required to send campaign`,
  ),
  [GqlUpsertTemplateInvalidContentFailureTypes.MISSING_MESSAGE_OR_SILENT_PUSH]:
    constructErrorMessage(!!fieldName, 'Add text to the message body, or check Deliver silently.'),
});

const constructFriendlySnippetErrorMessages = (
  fieldName: FieldNames | undefined,
  snippetNames: string[] | undefined,
): Record<
  Extract<
    GqlUpsertTemplateInvalidContentFailureTypes,
    | GqlUpsertTemplateInvalidContentFailureTypes.MISSING_SNIPPETS
    | GqlUpsertTemplateInvalidContentFailureTypes.INVALID_SNIPPET_PARAMS
  >,
  string
> => {
  const snippetNameString = snippetNames?.join(', ') ?? [];
  return {
    [GqlUpsertTemplateInvalidContentFailureTypes.MISSING_SNIPPETS]: constructErrorMessage(
      !!fieldName && !!snippetNames?.length,
      `Snippet: ${snippetNameString} in ${getFriendlyFieldName(
        fieldName,
      )} does not exist in your project`,
    ),
    [GqlUpsertTemplateInvalidContentFailureTypes.INVALID_SNIPPET_PARAMS]: constructErrorMessage(
      !!fieldName && !!snippetNames?.length,
      `Snippet: ${snippetNameString} in ${getFriendlyFieldName(
        fieldName,
      )} is missing at least one variable`,
    ),
  };
};

/**
 * Takes in validation failure errors and separates the errors by categories
 */
export const constructErrorValidations = (
  errors: ValidationFailureErrors[],
  locale: string,
  templateType?: TemplateType,
): ErrorValidationConstructReturn => {
  const contentErrors: ValidationError[] = [];
  const contentWarnings: ValidationError[] = [];

  // currently there are some errors where it's unknown where the `fieldName` is from so using 'email' as default for now
  if (errors) {
    errors.forEach((error) => {
      error.reasons.forEach((reason) => {
        if (
          GQL_CONTENT_ERRORS.includes(
            reason.__typename as GqlUpsertTemplateInvalidContentFailureTypes,
          )
        ) {
          const errorContainer = contentErrors;
          const organizedContentErrors = [
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.UNRENDERABLE,
              key: convertKeyType('invalid'),
              fieldName: reason?.fieldName,
              message: constructFriendlyFieldNameErrorMessages(reason?.fieldName as FieldNames)[
                GqlUpsertTemplateInvalidContentFailureTypes.UNRENDERABLE
              ],
              subMessage: reason?.msg,
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.UNRENDERABLE_WITH_INDEX,
              key: convertKeyType('invalid'),
              fieldName: reason?.fieldName,
              message: constructFriendlyFieldNameErrorMessages(reason?.fieldName as FieldNames)[
                GqlUpsertTemplateInvalidContentFailureTypes.UNRENDERABLE_WITH_INDEX
              ],
              index: reason?.index,
              subMessage: reason?.msg,
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.MISSING_SNIPPETS,
              key: convertKeyType('snippets'),
              fieldName: reason?.fieldName,
              message: constructFriendlySnippetErrorMessages(
                reason?.fieldName as FieldNames,
                reason?.snippets,
              )[GqlUpsertTemplateInvalidContentFailureTypes.MISSING_SNIPPETS],
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.INVALID_SNIPPET_PARAMS,
              key: convertKeyType('snippetsWithParams'),
              fieldName: reason?.fieldName,
              message: constructFriendlySnippetErrorMessages(
                reason?.fieldName as FieldNames,
                reason?.snippetsWithParams,
              )[GqlUpsertTemplateInvalidContentFailureTypes.INVALID_SNIPPET_PARAMS],
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.INVALID_ACTION_BUTTON_URI,
              key: convertKeyType('__typename'),
              fieldName: FieldNames.ACTION_BUTTONS,
              invalidButtonActions: reason?.invalidButtonActions,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_ACTION_BUTTON_URI
                ],
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.INVALID_DEFAULT_ACTION,
              key: convertKeyType('__typename'),
              fieldName: FieldNames.DEFAULT_ACTION,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_DEFAULT_ACTION
                ],
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.INVALID_EMAIL,
              key: convertKeyType('fieldName'),
              fieldName: reason?.fieldName,
              message: constructFriendlyFieldNameErrorMessages(reason?.fieldName as FieldNames)[
                GqlUpsertTemplateInvalidContentFailureTypes.INVALID_EMAIL
              ],
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.INVALID_HANDLEBAR_COUNT,
              key: convertKeyType('fieldName'),
              fieldName: reason?.fieldName,
              index: reason?.index,
              message: constructFriendlyFieldNameErrorMessages(reason?.fieldName as FieldNames)[
                GqlUpsertTemplateInvalidContentFailureTypes.INVALID_HANDLEBAR_COUNT
              ],
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.INVALID_HANDLEBAR_COMPLEXITY,
              key: convertKeyType('fieldName'),
              fieldName: reason?.fieldName,
              index: reason?.index,
              message: constructFriendlyFieldNameErrorMessages(reason?.fieldName as FieldNames)[
                GqlUpsertTemplateInvalidContentFailureTypes.INVALID_HANDLEBAR_COMPLEXITY
              ],
              subMessage: `Only merge parameters are allowed in the ${getFriendlyFieldName(
                reason?.fieldName as FieldNames,
              )}`,
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.INVALID_IMAGE_EXTENSION,
              key: convertKeyType('__typename'),
              fieldName: FieldNames.HEADER,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_IMAGE_EXTENSION
                ],
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.INVALID_URL_FORMAT,
              key: convertKeyType('__typename'),
              fieldName: FieldNames.HEADER,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_URL_FORMAT
                ],
              subMessage: 'Image URL must start with https:// or http://',
            },
            {
              errorType:
                GqlUpsertTemplateInvalidContentFailureTypes.INVALID_WHATSAPP_BUTTON_URL_FORMAT,
              key: convertKeyType('__typename'),
              fieldName: FieldNames.BUTTONS,
              index: reason?.index,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_WHATSAPP_BUTTON_URL_FORMAT
                ],
              subMessage: 'Button URL must start with https:// or http://',
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.INVALID_IMAGE_SIZE,
              key: convertKeyType('__typename'),
              fieldName: FieldNames.HEADER,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_IMAGE_SIZE
                ],
              subMessage:
                'Image size must be less than 16 MB for Business Initiated Templates and 5 MB for User Initiated Templates',
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.INVALID_SNIPPET_USAGE,
              key: convertKeyType('fieldName'),
              fieldName: reason?.fieldName,
              index: reason?.index,
              message: constructFriendlyFieldNameErrorMessages(reason?.fieldName as FieldNames)[
                GqlUpsertTemplateInvalidContentFailureTypes.INVALID_SNIPPET_USAGE
              ],
              subMessage: `Snippets not allowed in ${
                templateType === TemplateType.BusinessInitiated ? 'business' : 'user'
              }-initiated templates`,
            },
            {
              errorType:
                GqlUpsertTemplateInvalidContentFailureTypes.INVALID_WHATSAPP_BUTTON_URL_PARAMS,
              key: convertKeyType('__typename'),
              fieldName: FieldNames.BUTTONS,
              index: reason?.index,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_WHATSAPP_BUTTON_URL_PARAMS
                ],
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.EMOJI_IN_BUTTON_TEXT,
              key: convertKeyType('__typename'),
              fieldName: FieldNames.BUTTONS,
              index: reason?.index,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.EMOJI_IN_BUTTON_TEXT
                ],
              subMessage: 'Emojis not allowed in business-initiated template buttons',
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.WHATSAPP_EMOJI_LIMIT_EXCEEDED,
              key: convertKeyType('__typename'),
              fieldName: FieldNames.MESSAGE,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.WHATSAPP_EMOJI_LIMIT_EXCEEDED
                ],
            },
            {
              errorType:
                GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_BUTTON_PHONE_NUMBER,
              key: convertKeyType('__typename'),
              fieldName: FieldNames.BUTTONS,
              index: reason?.index,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_BUTTON_PHONE_NUMBER
                ],
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_BUTTON_TEXT,
              key: convertKeyType('__typename'),
              fieldName: FieldNames.BUTTONS,
              index: reason?.index,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_BUTTON_TEXT
                ],
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_BUTTON_URL,
              key: convertKeyType('__typename'),
              fieldName: FieldNames.BUTTONS,
              index: reason?.index,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_BUTTON_URL
                ],
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_MESSAGE,
              key: convertKeyType('__typename'),
              fieldName: FieldNames.MESSAGE,
              message: `${
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_MESSAGE
                ]
              }${
                templateType === TemplateType.BusinessInitiated
                  ? ' to submit template for review'
                  : ' to send campaign'
              }`,
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.INVALID_WHATSAPP_FOOTER,
              key: convertKeyType('__typename'),
              fieldName: FieldNames.FOOTER,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_WHATSAPP_FOOTER
                ],
              subMessage: 'Handlebars, data feeds, tabs, emojis, or new lines are not allowed',
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.INVALID_MEDIA_URL,
              key: convertKeyType('__typename'),
              fieldName: FieldNames.ATTACHMENT_URL,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_MEDIA_URL
                ],
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.INVALID_MESSAGE_TYPE_ID,
              key: convertKeyType('__typename'),
              fieldName: FieldNames.MESSAGE_TYPE,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_MESSAGE_TYPE_ID
                ],
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.MISSING_LINK,
              key: convertKeyType('__typename'),
              fieldName: FieldNames.HTML,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.MISSING_LINK
                ],
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.MISSING_UNSUBSCRIBE_URL,
              key: convertKeyType('__typename'),
              fieldName: FieldNames.HTML,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.MISSING_UNSUBSCRIBE_URL
                ],
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.AMBIGUOUS_EXPIRATION,
              key: convertKeyType('__typename'),
              fieldName: FieldNames.EXPIRATION_DATE_TIME_ABSOLUTE,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.AMBIGUOUS_EXPIRATION
                ],
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.INVALID_EXPIRATION,
              key: convertKeyType('__typename'),
              fieldName: FieldNames.EXPIRATION_DATE_TIME_ABSOLUTE,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_EXPIRATION
                ],
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.INVALID_IMAGE_URI,
              key: convertKeyType('__typename'),
              contentId: error.contentId,
              fieldName: FieldNames.ROKU_FHD_IMAGE,
              isRoku: true,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_IMAGE_URI
                ],
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.INVALID_ROKU_FONT_URI,
              key: convertKeyType('__typename'),
              contentId: error.contentId,
              fieldName: FieldNames.ROKU_TITLE_FONT,
              isRoku: true,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_ROKU_FONT_URI
                ],
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.INVALID_ROKU_HEX_VALUE,
              key: convertKeyType('__typename'),
              contentId: error.contentId,
              fieldName: FieldNames.ROKU_BACKGROUND_COLOR,
              isRoku: true,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_ROKU_HEX_VALUE
                ],
            },
          ];

          organizedContentErrors.forEach(
            ({
              errorType,
              fieldName,
              invalidButtonActions,
              isRoku,
              key,
              message,
              subMessage,
              index,
            }) =>
              handleContentValidationPush({
                errorContainer,
                errorType,
                fieldName,
                invalidButtonActions,
                isRoku,
                key,
                locale,
                message,
                reason,
                subMessage,
                index,
              }),
          );
        }

        if (
          GQL_CONTENT_WARNINGS.includes(
            reason.__typename as GqlUpsertTemplateInvalidContentFailureTypes,
          )
        ) {
          const key: keyof FailureErrorReason = '__typename';
          const errorContainer = contentWarnings;
          const selectedFailureType =
            reason?.fieldName === FieldNames.MISSING_MESSAGE_OR_SILENT_PUSH
              ? GqlUpsertTemplateInvalidContentFailureTypes.MISSING_MESSAGE_OR_SILENT_PUSH
              : GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_CONTENT;

          const organizedContentWarnings = [
            {
              errorType: selectedFailureType,
              fieldName: reason?.fieldName,
              message: constructFriendlyFieldNameErrorMessages(reason?.fieldName as FieldNames)[
                selectedFailureType
              ],
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.PREHEADER_TOO_LONG,
              fieldName: FieldNames.PREHEADER_TEXT,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.PREHEADER_TOO_LONG
                ],
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.INVALID_SUBJECT,
              fieldName: FieldNames.SUBJECT,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_SUBJECT
                ],
            },
            {
              errorType: GqlUpsertTemplateInvalidContentFailureTypes.INVALID_REQUIRED_ROKU_CONTENT,
              key: convertKeyType('__typename'),
              contentId: error.contentId,
              message:
                USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER[
                  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_REQUIRED_ROKU_CONTENT
                ],
            },
          ];

          organizedContentWarnings.forEach(({ errorType, fieldName, message }) =>
            handleContentValidationPush({
              reason,
              errorType,
              key,
              errorContainer,
              fieldName,
              locale,
              message,
            }),
          );
        }
      });
    });
  }

  return {
    contentErrors,
    contentWarnings,
  };
};

export const hasFieldError = (errors: ValidationError[], fieldName: FieldNames) =>
  !!errors.find((error) => error.fieldName === fieldName);

export const getFieldError = (errors: ValidationError[], fieldName: FieldNames) =>
  errors.find((error) => error.fieldName === fieldName);

export const constructFieldErrors = ({
  contentErrors,
  contentWarnings,
  fieldName,
}: ErrorValidationFieldNameConstruct): {
  hasError: boolean;
  hasWarning: boolean;
  validationError?: ValidationError;
} => ({
  hasError: hasFieldError(contentErrors, fieldName),
  hasWarning: hasFieldError(contentWarnings, fieldName),
  validationError: getFieldError(contentErrors, fieldName),
});

export const useFieldErrorTheme = (
  hasError: boolean,
  hasWarning: boolean,
): ErrorThemeDisplay | undefined => {
  if (!hasError && !hasWarning) return;
  if (hasError) {
    return errorThemes[ERROR_TYPES.CONTENT_ERROR];
  } else if (hasWarning) {
    return errorThemes[ERROR_TYPES.CONTENT_WARNING];
  }
};

/** This function finds url in the string, then replace the url with shorten url.
 * @param message A string that contains url and message.
 * @param domain A domain that's selected from dropdown on Shorten and track. Default is itbl.co
 * @returns result A string that contains shortened url and message.
 */
export const shortenLinkInMessage = (
  message: string | undefined = '',
  domain: string,
  projectId: number | '',
  minLength: number = 5,
  shortenAllLinks: boolean = false,
) => {
  const exampleString = `2NMwZ8b551bePs4T70747074`;
  const currExample = exampleString.slice(0, minLength);
  const nonEmptyDomain = domain || DEFAULT_LINK_SHORTENING_TRACKING_DOMAIN;

  if (!message) return;
  let shortenedPrefix = '-KT';
  if (projectId && projectId < 65) {
    shortenedPrefix = '-';
  } else if (projectId && projectId < 4096) {
    shortenedPrefix = '-K';
  }
  const urlRegex = /(((https?:\/\/)|(www\.))[^\s]+)/g;
  const alwaysReplaceRegex =
    /(((https?:\/\/)|(www\.))[^\s]+\/e\/encryptedConfirmUnsubscribeByPhone[^\s]+)/g;
  let domainWithParam;
  if (nonEmptyDomain.includes('https://')) {
    domainWithParam = nonEmptyDomain.concat(`/${shortenedPrefix}~${currExample}`);
  } else {
    domainWithParam = `https://${nonEmptyDomain}/${shortenedPrefix}~${currExample}`;
  }
  const alwaysShortenReplaced = message.replace(alwaysReplaceRegex, domainWithParam);
  return shortenAllLinks
    ? alwaysShortenReplaced.replace(urlRegex, domainWithParam)
    : alwaysShortenReplaced;
};

export const filterDesignErrors = (errors: ValidationError[]) =>
  errors
    .filter(
      (error) =>
        SUPPORTED_DESIGN_ERROR_FIELD_NAMES.includes(error.fieldName) ||
        error.fieldName === FieldNames.HTML_DATA_FEEDS,
    )
    .map((error) => ({ ...error }));

export const constructDesignErrors = (warnings: ValidationError[], errors: ValidationError[]) => {
  // @ts-ignore
  const designContentWarnings: ValidationError[] = filterDesignErrors(warnings);
  const designContentErrors: ValidationError[] = filterDesignErrors(errors);
  return {
    designContentErrors,
    designContentWarnings,
  };
};

export const getContentId = (mediumData: ContentIdKey): number => {
  if (!mediumData) return 0;
  const contentIdKey = Object.keys(mediumData).find((key) =>
    key.toLowerCase().includes('contentid'),
  ) as keyof ContentIdKey;

  return mediumData[contentIdKey] ?? 0;
};

export const getSettingsLocaleErrors = (errors: ValidationError[], warnings: ValidationError[]) => {
  const designErrors = filterDesignErrors(errors).map((error) => JSON.stringify(error));
  const designWarnings = filterDesignErrors(warnings).map((warning) => JSON.stringify(warning));

  const settingsLocaleContentWarnings = warnings.filter(
    (warning) => !designWarnings.includes(JSON.stringify(warning)),
  );
  const settingsLocaleContentErrors = errors.filter((error) => {
    return !designErrors.includes(JSON.stringify(error));
  });
  return {
    settingsLocaleContentErrors,
    settingsLocaleContentWarnings,
  };
};

export const getOtherLocaleError = (locale: string, errors: string[]) => {
  const localeErrors = errors.filter((error) => error !== locale);
  return localeErrors?.length > 0;
};

export const getOtherLocaleErrorNames = (
  errors: ValidationError[],
  currentContentId: number,
  defaultLocale: string,
) => {
  const otherLocaleErrorNames = errors
    .filter((error) => error.contentId !== currentContentId)
    .map(({ locale }) => (locale === '' ? defaultLocale : locale));
  return otherLocaleErrorNames;
};

type GetContactCardContentData = {
  displayName?: string;
  website?: string;
  imageUrl?: string;
  subheader?: string;
  email?: string;
};

export const getContactCardContentData = (
  contactCard: ContactCard,
  userData: Record<string, any>,
): GetContactCardContentData => {
  const templateFields = ['displayName', 'website', 'imageUrl', 'subheader', 'email'];
  const handlebarRegex = /{{([^{}]*)}}/g;

  return (
    contactCard &&
    templateFields.reduce((populated, field) => {
      /**
       * hits is an array of the template field and it's associated value.
       * we are looking for the value to be a handlebar to extrapolate the value in the "newValue" const.
       */
      const hits = [
        ...((contactCard[field as keyof ContactCard] as string)?.matchAll(handlebarRegex) || []),
      ];

      const newValue = hits.reduce(
        (template, hit) => {
          const trimmedValue = hit?.[1]?.trim(); // removes whitespace at beginning and end of handlebar.
          return (template as string).replace(hit[0], get(userData, trimmedValue, hit[0]));
        },
        contactCard[field as keyof ContactCard],
      );

      return {
        ...populated,
        [field]: newValue,
      };
    }, {})
  );
};

export const getPreviewWithDataError = (errorMessage: string) => {
  const errorType = errorMessage.split(':')[0];
  switch (errorType) {
    case PreviewWithDataErrorTypes.HANDLEBARS_EXCEPTION:
      return {
        label: `${displayContent.errPreviewWithData} ${displayContent.errHandlebarsException}`,
        errorMessage,
      };
    case PreviewWithDataErrorTypes.SEND_ABORTED_EXCEPTION:
      return {
        label: `${displayContent.errPreviewWithData} ${displayContent.errSendAbortedException}`,
        errorMessage,
      };
    default:
      return {
        label: displayContent.errPreviewWithData,
        errorMessage,
      };
  }
};
export const getUuidPromise = (uuid: string) =>
  new Promise((resolve: (value: string) => void, reject) => {
    getUserData(uuid, UserProfileDataType.UserId)
      .then(({ data }: { data: { userId: string } }) => {
        resolve(data.userId as string);
      })
      .catch((err) => {
        reject(err);
      });
  });

export const getCorrectlyCasedLocale = (currLocale: string, projectLocales: Locale[]) =>
  projectLocales.find((locale) => (locale?.tag?.toLowerCase() || '') === currLocale?.toLowerCase())
    ?.tag || '';

export const validateUrl = (str: string) => {
  const checkUrl = new RegExp('^(http|https)://', 'i');
  return checkUrl.test(str) ? str : `https://${str}`;
};

// append with "#" (#FFFFFF)
export const getIsInvalidHexColor = (hexColor?: string) =>
  !hexColor || (hexColor.length !== 4 && hexColor.length !== 7);

/*
export const getIsInvalidHexColor = (hexColor?: string) => {
	const regex = new RegExp(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/);
	return regex.test(str);
}
 */

export const getInitTemplateTrigger = (isTemplateFirstLoad: boolean, isLocaleUpdate: boolean) => {
  if (isTemplateFirstLoad) {
    return InitTemplateTrigger.TemplateFirstLoad;
  } else if (isLocaleUpdate) {
    return InitTemplateTrigger.LocaleUpdate;
  }
  return InitTemplateTrigger.TemplateUpdate;
};

export const checkIfLocaleExists = (loc: Locale, tempLocales: Locale[]) => {
  return tempLocales.some((obj) => obj.tag === loc.tag);
};

export const sendProofFilter = (searchTerm: string, list: ListOption[]) => {
  return (searchTerm
    ? list.filter(
        ({ label, id }) =>
          label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          id.includes(searchTerm.toLowerCase()),
      )
    : list) as unknown as BaseOption<string>[];
};

export const dedupeArray = (ary: any[]) => Array.from(new Set(ary));

// common hooks

export const useGetMessagingPermission = (
  messageMedium?: MessageMedium,
): MessagingPermissionOptions => {
  const permissions = useSelector(selectUserRbacPermissions);
  const { UseChannelBasedPermissions } = useSelector(selectFeatureFlags);
  const canViewMessaging = permissions.includes(PROJECT_PERMISSIONS.ViewMessaging);
  const canDraftMessaging = permissions.includes(PROJECT_PERMISSIONS.DraftMessaging);
  const canManageMessaging = canManageMessagingByMedium(
    permissions,
    messageMedium,
    UseChannelBasedPermissions,
  );
  if (canViewMessaging && !canDraftMessaging && !canManageMessaging) {
    return MessagingPermissionOptions.VIEW_ONLY;
  } else if (canViewMessaging && canDraftMessaging && !canManageMessaging) {
    return MessagingPermissionOptions.VIEW_AND_DRAFT;
  } else if (canViewMessaging && canDraftMessaging && canManageMessaging) {
    return MessagingPermissionOptions.ALL;
  } else {
    return MessagingPermissionOptions.NONE;
  }
};

/** Returns true if user profile and data permissions are granted */
export const useGetHasUserProfilePermission = (): boolean => {
  const permissions = useSelector(selectUserRbacPermissions);
  return permissions.includes(PROJECT_PERMISSIONS.ViewAndExportUserData);
};

export const useGetCanManageBaseTemplates = (): boolean => {
  const permissions = useSelector(selectUserRbacPermissions);
  return permissions.includes(PROJECT_PERMISSIONS.ManageBaseTemplates);
};

export const getMediumDataContentId = (
  medium: SelectedMessageMedium,
  liveTemplate: TemplateAndContentFormDataLocale,
) => {
  if (medium === MessageMedium.Email) {
    return (liveTemplate.mediumData as EmailContentDefinition).emailContentId;
  }
  if (medium === MessageMedium.WebPush) {
    return (liveTemplate.mediumData as WebPushContentDefinition).webPushContentId;
  }
  if (medium === MessageMedium.Push) {
    return (liveTemplate.mediumData as PushContentDefinition).pushContentId;
  }

  if (medium === MessageMedium.InApp) {
    return (liveTemplate.mediumData as InAppContentDefinition).inAppContentId;
  }
  if (medium === MessageMedium.SMS) {
    return (liveTemplate.mediumData as SMSContentDefinition).smsContentId;
  }
  if (medium === EmbeddedMessageMedium.Embedded) {
    return (liveTemplate.mediumData as EmbeddedContentDefinition).embeddedContentId;
  }
  return 'no-content-id-found';
};

/**
 * Returns non english locales
 * Currently locales are user-defined and can be named however they want. This function can only support Iterables guidelines of [ISO_LANGUAGE_CODE]-[IS_COUNTRY_CODE]
 * We are using a predetermined regex with 'en-' prefix that follows Iterable recommended guidelines for locale tags.
 */
export const getNonEnglishLocales = (activeTemplateLocales: string[]) => {
  const languageCodeRegex = /en-?/i;
  return activeTemplateLocales.filter((locale) => !languageCodeRegex.test(locale));
};

/**
 * NBA generated template names contain a prefix: [AI Retargeting]
 * The only way to determine that a template is AI generated is via naming convention
 * We want to grab non english locales that belong to a AI generated template
 */
export const getNBALocalesErrors = (templateName: string, activeTemplateLocales: string[]) => {
  if (templateName?.includes(NBA_PREFIX)) {
    return getNonEnglishLocales(activeTemplateLocales);
  }
  return [];
};

export const replaceSelectedTextWith = (
  value: string,
  selectionStart: number,
  selectionEnd: number,
  replaceWith: string,
) => {
  const beforeReplacement = value.slice(0, selectionStart);
  const afterReplacement = value.slice(selectionEnd);
  return [beforeReplacement, replaceWith, afterReplacement].join('');
};

export const getInputCursorPosition = (
  ref: RefObject<HTMLTextAreaElement | HTMLInputElement> | null,
) => ({
  selectionStart: ref?.current?.selectionStart ?? 0,
  selectionEnd: ref?.current?.selectionEnd ?? 0,
});

export const createInsertHandler = (
  ref: RefObject<HTMLTextAreaElement | HTMLInputElement> | null,
  value: string,
  onChange: (value: string) => void,
) => {
  const cursorPosition = getInputCursorPosition(ref);
  return function insert(content: string) {
    return onChange(
      replaceSelectedTextWith(
        value,
        cursorPosition.selectionStart,
        cursorPosition.selectionEnd,
        content,
      ),
    );
  };
};

export const aggregateErrors = (
  currentLocaleTag: string,
  validationFailures: GqlInvalidContentReason[],
  templateType?: TemplateType,
) => {
  const { contentErrors: localeContentErrors = [], contentWarnings: localeContentWarnings = [] } =
    constructErrorValidations([{ reasons: validationFailures }], currentLocaleTag, templateType);

  const { designContentErrors = [], designContentWarnings = [] } = constructDesignErrors(
    localeContentWarnings,
    localeContentErrors,
  );

  const { settingsLocaleContentErrors = [], settingsLocaleContentWarnings = [] } =
    getSettingsLocaleErrors(localeContentErrors, localeContentWarnings);

  return {
    designContentErrors,
    designContentWarnings,
    settingsLocaleContentErrors,
    settingsLocaleContentWarnings,
  };
};
